import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import SpecialCard from "@/components/feature/SpecialCard";
import MediaCard from "@/components/base/MediaCard";
import SectionHeading from "@/components/base/SectionHeading";
import { usePageMeta } from "@/hooks/usePageMeta";
import { heroSlides, mediaItems } from "@/mocks/media";
import { newsItems } from "@/mocks/news";
import { specialItems } from "@/mocks/specials";
import HeroCarousel from "./components/HeroCarousel";
import QuickLinks from "./components/QuickLinks";
import NewsSection from "./components/NewsSection";

const stats = [
  { value: "1,200+", label: "影视作品档案" },
  { value: "42", label: "类型与地区标签" },
  { value: "每日", label: "行业资讯更新" },
  { value: "12", label: "主题片单策划" },
];

export default function Home() {
  usePageMeta(
    "天马传媒影视作品聚合平台，精彩电影电视剧推荐",
    "天马传媒官网，提供丰富的电影、电视剧资源展示和最新影视资讯，满足用户多样化影视内容需求，打造专业影视内容聚合平台。",
    "天马传媒,影视作品,电影推荐,电视剧资讯,影视内容平台,影视作品聚合,影视资讯",
  );

  const movies = mediaItems
    .filter((item) => item.type === "movie")
    .sort((a, b) => b.hot - a.hot)
    .slice(0, 6);

  const series = mediaItems
    .filter((item) => item.type === "tv")
    .sort((a, b) => new Date(b.updated).getTime() - new Date(a.updated).getTime())
    .slice(0, 4);

  const ranking = [...mediaItems].sort((a, b) => b.hot - a.hot).slice(0, 5);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader transparent />
      <main>
        <HeroCarousel slides={heroSlides} />
        <QuickLinks />

        <section className="shell py-14 md:py-16">
          <SectionHeading
            eyebrow="热门电影"
            title="本周最受关注的院线作品"
            description="从科幻到悬疑，六部正在被反复讨论的电影，按站内热度排序。"
            actionLabel="全部电影"
            actionTo="/category/movie"
          />
          <div className="mt-7 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-6">
            {movies.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="shell py-14 md:py-16">
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="mb-2 flex items-center gap-2 text-xs tracking-[0.2em] text-accent-600">
                  <span className="h-px w-6 bg-current"></span>
                  推荐剧集
                </p>
                <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                  近期上新的华语剧集
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  按更新日期排列，涵盖悬疑、生活、武侠与科幻四种方向。
                </p>
              </div>
              <Link
                to="/category/tv"
                className="group inline-flex w-fit shrink-0 cursor-pointer items-center gap-1 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition hover:border-primary-300 hover:text-primary-600"
              >
                浏览剧集库
                <i className="ri-arrow-right-line transition group-hover:translate-x-1"></i>
              </Link>
            </div>

            <div className="mt-7 grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
              {series.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        </section>

        <section className="shell py-14 md:py-16">
          <SectionHeading
            eyebrow="影视专题"
            title="按主题聚合的片单"
            description="不想一部部挑？我们按主题把作品与资讯整理在一起。"
            actionLabel="专题总览"
            actionTo="/specials"
          />
          <div className="mt-7 grid gap-4 md:grid-cols-3 md:gap-5">
            {specialItems.map((special) => (
              <SpecialCard key={special.id} special={special} />
            ))}
          </div>
        </section>

        <div className="border-y border-background-200 bg-background-100">
          <NewsSection news={newsItems.slice(0, 5)} ranking={ranking} />
        </div>

        <section className="shell py-14 md:py-16">
          <div className="overflow-hidden rounded-lg bg-primary-950">
            <div className="grid gap-8 p-6 md:grid-cols-[1.3fr_1fr] md:gap-10 md:p-10 lg:p-12">
              <div>
                <p className="flex items-center gap-2 text-xs tracking-[0.24em] text-accent-300">
                  <span className="h-px w-6 bg-current"></span>
                  关于天马传媒
                </p>
                <h2 className="mt-4 font-heading text-2xl font-semibold leading-snug text-background-50 md:text-3xl">
                  做一份清楚、耐看的影视内容档案
                </h2>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-300">
                  天马传媒专注于影视作品的整理与推荐。我们为每一部作品建立完整档案，包含主创信息、剧情简介与相关动态，并用多维筛选与主题专题，帮助你在十分钟内找到今晚想看的那一部。
                </p>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-background-400">
                  本站仅提供内容展示与资讯聚合，不提供影片播放与下载。所有作品信息均为演示数据。
                </p>
                <Link
                  to="/about"
                  className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-primary-950 transition hover:bg-accent-400"
                >
                  了解平台更多
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-primary-800 bg-primary-800">
                {stats.map((stat) => (
                  <div key={stat.label} className="bg-primary-900 p-4 md:p-5">
                    <p className="font-heading text-xl font-semibold text-accent-300 md:text-2xl">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-xs text-background-400">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}